// Thin fetch wrapper for the admin API. Attaches the bearer token, parses
// JSON, and surfaces 401s by clearing auth + reloading the SPA — the
// AuthProvider will re-read localStorage and bounce to the login route.

const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  token?: string | null;
  // Caller opts out of the global 401 → logout dance, e.g. the login route
  // itself which deliberately handles 401 inline.
  noAuthRedirect?: boolean;
  // For multipart (firmware upload) — caller passes a FormData and we skip
  // the JSON serialization + content-type header.
  formData?: FormData;
}

const TOKEN_KEY = "krypton.token";

function clearAuthAndReload(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem("krypton.user");
  // Full reload re-runs AuthProvider's localStorage check and routes to /login.
  window.location.assign("/login");
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.token) headers["Authorization"] = `Bearer ${opts.token}`;

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body,
  });

  if (res.status === 401 && !opts.noAuthRedirect) {
    clearAuthAndReload();
    throw new ApiError(401, null, "unauthenticated");
  }

  let parsed: unknown = null;
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    parsed = await res.json();
  } else if (res.status !== 204) {
    parsed = await res.text();
  }

  if (!res.ok) {
    const msg =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `request failed (${res.status})`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as T;
}
